import { useState } from 'react';
import { Card, CardContent, Typography, Grid, Avatar, AvatarGroup, IconButton, Stack, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useSelector } from 'react-redux';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import AddUserIcon from '@mui/icons-material/PersonAddOutlined';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { SubscriptionTypes } from '../../types/subscription.types';
import { CustomerTypes } from '../../types';
import { MoreOptionsList } from '../lists/more-options';
import { CustomerService } from '../../services/customer.service';
import AreYouSureModal from '../are-you-sure-modal';
import { RootState } from '../../store';
import { InviteUserToSubscriptionModal } from '../invite-user-to-subscription-modal';
import { EditLicenseCountModal } from '../edit-license-count-modal';
import { permissionCheck } from '../../helpers/permissionCheck';
import { countLicensesConsumed } from '../../helpers/licenseUsage';
import { customPermissions, customerPermissionFlags } from '../../constants/systemPermissions.constants';

interface Props {
    subscription: SubscriptionTypes.Model,
    customer: CustomerTypes.Model,
    /** Fired after a POM Master edits the license count so a react-query owner can refetch. */
    onSubscriptionChanged?: () => void,
}

export const MySubscription: React.FC<Props> = ({
    subscription,
    customer,
    onSubscriptionChanged,
}) => {
    const { loading } = useSelector((state: RootState) => state.customer);
    const { authUser } = useSelector((state: RootState) => state.auth);
    const [changeInviteCodeModal, setChangeInviteCodeModal] = useState(false);
    const [inviteUserModal, setInviteUserModal] = useState(false);
    const [licenseCountModal, setLicenseCountModal] = useState(false);

    // ClickUp 868m4vzj7: only a POM Master in their own session edits the cap.
    // A masquerading POM Master carries the customer's permissions, and the
    // API refuses the write in that state too.
    const canEditLicenseCount = Boolean(authUser?.pomMaster) && !authUser?.masquarade;

    const onChangeInviteCode = async () => {
        await CustomerService.resetSubscriptionInviteCode(subscription.id)
    };

    return (
        <>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                </Grid>
                <Grid item xs={12}>
                </Grid>
                <Grid item xs={12} mt={2}>
                    <Typography fontSize={18} fontWeight={500} noWrap>
                        Subscription Details
                    </Typography>
                </Grid>
                <Grid container margin={1} bgcolor="background.neutral" padding={1} borderRadius='5px'>
                    <Grid item md={3} xs={12} justifyContent={'center'}>
                        <Typography variant="body2" color="text.secondary">
                            Licenses Consumed:
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2" color="text.primary" fontWeight={600}>
                                {`${countLicensesConsumed(subscription)}/${subscription?.licenseCount}`}
                            </Typography>
                            {canEditLicenseCount && (
                                <Tooltip title="Edit license count" placement="top">
                                    <IconButton
                                        size="small"
                                        aria-label="Edit license count"
                                        onClick={() => setLicenseCountModal(true)}
                                        sx={{ p: 0.25 }}
                                    >
                                        <EditIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            Status:
                        </Typography>
                        <Typography variant="body2" color="text.primary" fontWeight={600}>
                            {subscription.status}
                        </Typography>
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            Invite Code:
                        </Typography>
                        <Typography variant="body2" color="text.primary" fontWeight={600}>
                            {subscription?.inviteCode}
                        </Typography>
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            Expiration Date:
                        </Typography>
                        <Typography variant="body2" color="text.primary" fontWeight={600}>
                            {dayjs(subscription?.expiresAt).format('MM/DD/YYYY hh:mm A')}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>

            <AreYouSureModal
                title="Are You Sure?"
                description={`This will reset the invite code. Users will not be able to use the old invite code.`}
                visible={changeInviteCodeModal}
                loading={loading.changeInviteCode}
                onConfirm={async () => {
                    await onChangeInviteCode();
                    setChangeInviteCodeModal(false);
                }}
                onHide={() => {
                    setChangeInviteCodeModal(false);
                }}
            />

            <InviteUserToSubscriptionModal 
                setVisible={setInviteUserModal}
                subscriptionId={subscription?.id}
                visible={inviteUserModal}
            />

            {canEditLicenseCount && (
                <EditLicenseCountModal
                    subscription={subscription}
                    visible={licenseCountModal}
                    onClose={() => setLicenseCountModal(false)}
                    onSaved={() => onSubscriptionChanged?.()}
                />
            )}
        </>
    );
};